import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CurrencyInr, Sparkle, Upload, X as XIcon } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'joining_fee_inr', label: 'Joining Fee (INR)', type: 'number', min: 1 },
  { key: 'joining_offer_social_share_pct', label: 'Social-share % off', type: 'number', min: 0, max: 100 },
  { key: 'joining_offer_invite_pct', label: 'Invite-friends % off', type: 'number', min: 0, max: 100 },
  { key: 'joining_offer_invite_count', label: 'Invite friends required', type: 'number', min: 1 },
  { key: 'joining_offer_promo_pct', label: 'Promo % off', type: 'number', min: 0, max: 100 },
  { key: 'joining_offer_promo_label', label: 'Promo label (shown to user)', type: 'text' },
];

/**
 * Admin tile to configure the one-time joining fee + 3 discount offers
 * surfaced on the Vendor / Professional dashboards.
 */
const AdminJoiningFeePanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingShareImage, setUploadingShareImage] = useState(false);
  const fileInputRef = useRef(null);
  const BACKEND_URL_API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/joining/settings`, { withCredentials: true });
      setCfg(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load joining settings');
    }
  };

  useEffect(() => { load(); }, []);

  const handleShareImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingShareImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL_API}/api/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const absUrl = data.url?.startsWith('http') ? data.url : `${BACKEND_URL_API}${data.url}`;
      setCfg((c) => ({ ...c, joining_share_image_url: absUrl }));
      toast.success('Share image uploaded — remember to click Save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingShareImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(f => {
        let v = cfg[f.key];
        if (f.type === 'number') v = v === '' || v === null || v === undefined ? null : Number(v);
        if (v !== null && v !== undefined && v !== '') payload[f.key] = v;
      });
      payload.joining_offer_promo_active = !!cfg.joining_offer_promo_active;
      // Always send share fields (even empty strings, so admin can clear them)
      payload.joining_share_image_url = cfg.joining_share_image_url || '';
      payload.joining_share_caption = cfg.joining_share_caption || '';
      payload.joining_share_link = cfg.joining_share_link || '';
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/joining/settings`, payload, { withCredentials: true });
      setCfg(data);
      toast.success('Joining fee settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <Card data-testid="admin-joining-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Loading joining-fee settings...</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5] mb-6" data-testid="admin-joining-panel">
      <CardHeader className="border-b border-[#E8E1D5]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-2xl flex items-center gap-2">
              <CurrencyInr size={22} className="text-brand-primary" /> Joining Fee &amp; Offers
            </CardTitle>
            <CardDescription>
              Controls the one-time joining fee vendors / professionals pay before accepting orders. Three discount paths can be applied at checkout — exactly one per user.
            </CardDescription>
          </div>
          <Button onClick={handleSave} disabled={saving}
            className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
            data-testid="admin-joining-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-bold text-text-secondary">{f.label}</Label>
              <Input
                type={f.type}
                min={f.min} max={f.max}
                value={cfg[f.key] ?? ''}
                onChange={(e) => setCfg({ ...cfg, [f.key]: f.type === 'number' ? e.target.value : e.target.value })}
                className="rounded-none"
                data-testid={`admin-joining-input-${f.key}`}
              />
            </div>
          ))}
        </div>

        <div className="border-2 border-brand-accent/20 rounded p-3 bg-brand-accent/5 flex items-center justify-between"
          data-testid="admin-joining-promo-toggle">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-brand-primary" />
            <div>
              <div className="font-bold text-sm">Promo offer is {cfg.joining_offer_promo_active ? 'ACTIVE' : 'OFF'}</div>
              <div className="text-xs text-text-secondary">When ON, every eligible user sees the promo discount as a checkout option.</div>
            </div>
          </div>
          <Button
            variant={cfg.joining_offer_promo_active ? 'default' : 'outline'}
            onClick={() => setCfg({ ...cfg, joining_offer_promo_active: !cfg.joining_offer_promo_active })}
            className={cfg.joining_offer_promo_active
              ? 'bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none'
              : 'rounded-none border-[#E8E1D5]'}
            data-testid="admin-joining-promo-toggle-btn">
            {cfg.joining_offer_promo_active ? 'Turn OFF' : 'Turn ON'}
          </Button>
        </div>

        {/* --- Share content for the social-share offer ---------------- */}
        <div className="border-t border-[#E8E1D5] pt-4 space-y-3" data-testid="admin-joining-share-content">
          <div>
            <h3 className="font-heading text-lg flex items-center gap-2">
              <Sparkle size={18} className="text-purple-600" weight="duotone" /> Share Content (Social-Share Offer)
            </h3>
            <p className="text-xs text-text-secondary">
              When a vendor/pro taps <em>Share &amp; Pay</em> on the joining-fee step, the share sheet
              prefills with this image, caption, and link. Set ONE strong post that you want every new
              partner to broadcast in exchange for the discount.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Image upload */}
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-bold text-text-secondary">Share Image</Label>
              <input
                type="file" accept="image/*" ref={fileInputRef}
                onChange={handleShareImageUpload}
                className="hidden"
                data-testid="admin-share-image-file-input"
              />
              {cfg.joining_share_image_url ? (
                <div className="relative w-full aspect-square max-w-[160px] border border-[#E8E1D5] rounded overflow-hidden group">
                  <img src={cfg.joining_share_image_url} alt="Share preview"
                    className="w-full h-full object-cover" data-testid="admin-share-image-preview" />
                  <button
                    type="button"
                    onClick={() => setCfg({ ...cfg, joining_share_image_url: '' })}
                    className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                    data-testid="admin-share-image-clear-btn"
                    aria-label="Clear image">
                    <XIcon size={14} />
                  </button>
                </div>
              ) : null}
              <Button
                type="button" size="sm" variant="outline"
                disabled={uploadingShareImage}
                onClick={() => fileInputRef.current?.click()}
                className="rounded-none border-[#E8E1D5]"
                data-testid="admin-share-image-upload-btn">
                <Upload size={14} className="mr-2" />
                {uploadingShareImage ? 'Uploading…' : (cfg.joining_share_image_url ? 'Replace image' : 'Upload image')}
              </Button>
            </div>

            {/* Caption */}
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-text-secondary">
                Caption / Post Text
              </Label>
              <Textarea
                rows={5}
                value={cfg.joining_share_caption || ''}
                onChange={(e) => setCfg({ ...cfg, joining_share_caption: e.target.value })}
                placeholder="Hey! I just joined BeeWeddy — your one-stop wedding & event booking platform. Sign up here:"
                className="rounded-none resize-none"
                maxLength={800}
                data-testid="admin-share-caption-input"
              />
              <div className="text-[11px] text-text-secondary text-right">
                {(cfg.joining_share_caption || '').length}/800
              </div>

              <Label className="text-xs uppercase tracking-wider font-bold text-text-secondary pt-2 block">
                Share Link / Page URL
              </Label>
              <Input
                type="url"
                value={cfg.joining_share_link || ''}
                onChange={(e) => setCfg({ ...cfg, joining_share_link: e.target.value })}
                placeholder="https://beeweddy.com/share/welcome"
                className="rounded-none"
                data-testid="admin-share-link-input"
              />
              <p className="text-[11px] text-text-secondary">
                The destination link that gets appended to the shared message.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminJoiningFeePanel;
